/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import {
  openStatementPdfInBrowser,
  STORAGE_PERMISSION_ERROR_NAME,
} from "@farmlands/account-management";

const createStoragePermissionError = (): Error => {
  const error = new Error("Storage permission error");
  error.name = STORAGE_PERMISSION_ERROR_NAME;
  return error;
};

const isStoragePermissionError = (err: unknown): boolean =>
  err instanceof Error && err.name === STORAGE_PERMISSION_ERROR_NAME;

export type SaveBlobToDeviceParams = {
  blob: Blob;
  filename: string;
  contentType?: string;
  viewWindow?: Window | null;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read blob"));
        return;
      }
      const base64 = result.slice(result.indexOf(",") + 1);
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const saveOnWeb = ({ blob, filename }: SaveBlobToDeviceParams): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const getNativeDownloadPath = (filename: string): { path: string; directory: Directory } => {
  if (Capacitor.getPlatform() === "android") {
    return { path: `Download/${filename}`, directory: Directory.Documents };
  }

  return { path: filename, directory: Directory.Documents };
};

const splitFilename = (filename: string): { basename: string; extension: string } => {
  const dotIndex = filename.lastIndexOf(".");

  if (dotIndex <= 0) {
    return { basename: filename, extension: "" };
  }

  return {
    basename: filename.slice(0, dotIndex),
    extension: filename.slice(dotIndex),
  };
};

const nativeFileExists = async (path: string, directory: Directory): Promise<boolean> => {
  try {
    await Filesystem.stat({ path, directory });
    return true;
  } catch {
    return false;
  }
};

const resolveAvailableFilename = async (
  filename: string,
  fileExists: (candidate: string) => Promise<boolean>
): Promise<string> => {
  if (!(await fileExists(filename))) {
    return filename;
  }

  const { basename, extension } = splitFilename(filename);

  let counter = 1;
  while (true) {
    const candidate = `${basename} (${counter})${extension}`;

    if (!(await fileExists(candidate))) {
      return candidate;
    }

    counter += 1;
  }
};

const resolveAvailableNativePath = async (
  filename: string
): Promise<{ path: string; directory: Directory }> => {
  const { directory } = getNativeDownloadPath(filename);

  const availableFilename = await resolveAvailableFilename(
    filename,
    async (candidate: string): Promise<boolean> => {
      const { path } = getNativeDownloadPath(candidate);
      return nativeFileExists(path, directory);
    }
  );

  return getNativeDownloadPath(availableFilename);
};

const ensureStoragePermission = async (): Promise<void> => {
  try {
    const permission = await Filesystem.requestPermissions();
    if (permission.publicStorage !== "granted") {
      throw createStoragePermissionError();
    }
  } catch (err) {
    if (isStoragePermissionError(err)) {
      throw err;
    }

    throw createStoragePermissionError();
  }
};

const writeNativeFile = async ({
  path,
  directory,
  data,
}: {
  path: string;
  directory: Directory;
  data: string;
}): Promise<{ uri: string }> => {
  await ensureStoragePermission();

  try {
    return await Filesystem.writeFile({
      path,
      data,
      directory,
      recursive: true,
    });
  } catch {
    throw createStoragePermissionError();
  }
};

/** Saves exported files to the device without opening them. */
const saveOnNativeToDevice = async ({ blob, filename }: SaveBlobToDeviceParams): Promise<void> => {
  const base64 = await blobToBase64(blob);
  const { path, directory } = await resolveAvailableNativePath(filename);

  await writeNativeFile({ path, directory, data: base64 });
};

/** Writes a PDF to the device and opens it in the system viewer. */
const openOnNative = async ({
  blob,
  filename,
  contentType,
}: SaveBlobToDeviceParams): Promise<void> => {
  const base64 = await blobToBase64(blob);
  const written = await writeNativeFile({
    path: filename,
    data: base64,
    directory: Directory.Documents,
  });

  try {
    await FileOpener.open({
      filePath: written.uri,
      contentType: contentType ?? blob.type ?? "application/pdf",
      openWithDefault: true,
    });
  } catch {
    throw createStoragePermissionError();
  }
};

export const saveBlobToDevice = async (params: SaveBlobToDeviceParams): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    await saveOnNativeToDevice(params);
  } else {
    saveOnWeb(params);
  }
};

export const viewBlobInBrowser = async (params: SaveBlobToDeviceParams): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    try {
      await openOnNative(params);
    } finally {
      params.viewWindow?.close();
    }
    return;
  }

  try {
    openStatementPdfInBrowser({ blob: params.blob, viewWindow: params.viewWindow });
  } catch (err) {
    params.viewWindow?.close();
    throw err;
  }
};
